import { DiffOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Button, Card, Col, Drawer, Empty, Form, InputNumber, message,
  Popconfirm, Row, Select, Space, Spin, Table, Tag, Typography,
  type TableProps,
} from "antd";
import { useMemo, useState } from "react";
import { STATUS_LABELS, type GuideListItem } from "@/features/guides/api";
import {
  createEvaluation, deleteEvaluation, getTemplate, listEvaluationsByGuide,
  listExperts, listTemplates, submitEvaluation, upsertEvaluationEntries,
  type EvaluationOut, type ExpertOut, type TemplateDetail,
} from "./api";
import { GUIDE_PICKER_KEY, GuidePicker } from "./GuidePicker";

// 左侧:可搜索/分页的指南选择器;右侧:选中指南的评审列表 + 操作。

/** 专家列展示:expert_id 为 `ai:<model>` 前缀时显示 AI(模型名);否则按专家列表 id→姓名映射。 */
function expertDisplayName(expertId: string, experts: ExpertOut[] | undefined): string {
  if (expertId.startsWith("ai:")) return `AI(${expertId.slice(3)})`;
  const e = (experts ?? []).find((x) => String(x.id) === expertId);
  return e ? (e.real_name ?? e.username) : expertId;
}

export function EvaluationsPage() {
  const [selectedGuide, setSelectedGuide] = useState<GuideListItem | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [scoringEval, setScoringEval] = useState<EvaluationOut | null>(null);

  return (
    <Row gutter={12}>
      <Col xs={24} md={10} lg={8}>
        <GuidePicker
          title="指南列表"
          selectedId={selectedGuide?.id ?? null}
          onSelect={setSelectedGuide}
        />
      </Col>
      <Col xs={24} md={14} lg={16}>
        {selectedGuide ? (
          <EvaluationsPanel
            guide={selectedGuide}
            onAssign={() => setAssignOpen(true)}
            onScore={setScoringEval}
          />
        ) : (
          <Card>
            <Empty description="左侧选择一条指南查看其评审" />
          </Card>
        )}
      </Col>

      {selectedGuide && assignOpen && (
        <AssignDrawer
          guide={selectedGuide}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
        />
      )}
      {scoringEval && (
        <ScoringDrawer
          evaluation={scoringEval}
          open
          onClose={() => setScoringEval(null)}
        />
      )}
    </Row>
  );
}

function EvaluationsPanel({
  guide,
  onAssign,
  onScore,
}: {
  guide: GuideListItem;
  onAssign: () => void;
  onScore: (ev: EvaluationOut) => void;
}) {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [compareOpen, setCompareOpen] = useState(false);

  const evalsQ = useQuery({
    queryKey: ["evaluations", guide.id],
    queryFn: () => listEvaluationsByGuide(guide.id),
  });
  const expertsQ = useQuery({ queryKey: ["experts"], queryFn: listExperts });
  const submitM = useMutation({
    mutationFn: submitEvaluation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluations", guide.id] });
      // 提交后 guide 状态/分数已变(Apply→Scored),刷新左侧选择器让标签跟着更新
      qc.invalidateQueries({ queryKey: [GUIDE_PICKER_KEY] });
      msgApi.success("已提交");
    },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const delM = useMutation({
    mutationFn: deleteEvaluation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evaluations", guide.id] });
      qc.invalidateQueries({ queryKey: [GUIDE_PICKER_KEY] });
      msgApi.success("已删除");
    },
  });

  return (
    <>
      <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text>评审管理 · {guide.title}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {guide.reg_no} · {STATUS_LABELS[guide.status]}
            {evalsQ.data?.[0]?.is_divergent && (
              <Tag color="red" style={{ marginLeft: 6 }}>
                总分分歧 {evalsQ.data[0].diff_score} 分
              </Tag>
            )}
          </Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<DiffOutlined />}
            onClick={() => setCompareOpen(true)}
            disabled={!evalsQ.data?.length}
          >
            对比视图
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAssign}>
            分配专家
          </Button>
        </Space>
      }
    >
      {msgCtx}
      <Table
        size="small"
        rowKey="id"
        loading={evalsQ.isLoading}
        dataSource={evalsQ.data ?? []}
        columns={[
          {
            title: "专家",
            dataIndex: "expert_id",
            width: 160,
            render: (id: string) => expertDisplayName(id, expertsQ.data),
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 90,
            render: (s: string) => (
              <Tag color={s === "Apply" ? "green" : s === "Waiting" ? "orange" : "default"}>
                {s === "Waiting" ? "待评" : s === "Apply" ? "已提交" : s}
              </Tag>
            ),
          },
          { title: "分数", dataIndex: "score", width: 80 },
          { title: "等级", dataIndex: "level", width: 70 },
          {
            title: "操作",
            width: 220,
            render: (_: unknown, row: EvaluationOut) => (
              <Space size={4}>
                <Button size="small" onClick={() => onScore(row)}>
                  {row.status === "Apply" ? "查看" : "打分"}
                </Button>
                {row.status === "Waiting" && (
                  <Popconfirm title="确认提交?" onConfirm={() => submitM.mutate(row.id)}>
                    <Button size="small" type="primary" icon={<SendOutlined />}>提交</Button>
                  </Popconfirm>
                )}
                <Popconfirm title="删除?" onConfirm={() => delM.mutate(row.id)}>
                  <Button size="small" danger>删</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Card>
      {compareOpen && (
        <CompareDrawer
          guide={guide}
          evaluations={evalsQ.data ?? []}
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </>
  );
}

function AssignDrawer({
  guide,
  open,
  onClose,
}: {
  guide: GuideListItem;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [form] = Form.useForm();

  const expertsQ = useQuery({ queryKey: ["experts"], queryFn: listExperts });
  const tmplQ = useQuery({
    queryKey: ["templates"],
    queryFn: () => listTemplates({ page: 1, page_size: 100 }),
  });

  const mutation = useMutation({
    mutationFn: (body: { expert_id: string; template_id: string }) =>
      createEvaluation({ ...body, guide_id: guide.id }),
    onSuccess: () => {
      msgApi.success("已分配");
      qc.invalidateQueries({ queryKey: ["evaluations", guide.id] });
      onClose();
    },
    onError: (e: Error) => msgApi.error(e.message),
  });

  return (
    <Drawer open={open} onClose={onClose} title={`分配评审:${guide.title}`} width={480}>
      {msgCtx}
      <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
        <Form.Item
          name="expert_id" label="专家" rules={[{ required: true }]}
        >
          <Select
            loading={expertsQ.isLoading}
            placeholder="选择专家"
            options={(expertsQ.data ?? []).map((e) => ({
              value: String(e.id),
              label: `${e.real_name ?? e.username}(@${e.username})`,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="template_id" label="评价模板" rules={[{ required: true }]}
        >
          <Select
            loading={tmplQ.isLoading}
            placeholder="选择模板"
            options={(tmplQ.data?.items ?? []).map((t) => ({
              value: t.id,
              label: t.name,
            }))}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={mutation.isPending}>
          确认分配
        </Button>
      </Form>
    </Drawer>
  );
}

function ScoringDrawer({
  evaluation,
  open,
  onClose,
}: {
  evaluation: EvaluationOut;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();

  const tmplQ = useQuery({
    queryKey: ["template-detail", evaluation.template_id],
    queryFn: () => getTemplate(evaluation.template_id),
  });

  const initScores = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of evaluation.entries) {
      if (e.score != null) map[`${e.item_id}-${e.entry_id}`] = Number(e.score);
    }
    return map;
  }, [evaluation]);
  const [scores, setScores] = useState<Record<string, number>>(initScores);

  const saveM = useMutation({
    mutationFn: (body: Array<{ item_id: string; entry_id: string; score: number }>) =>
      upsertEvaluationEntries(evaluation.id, body),
    onSuccess: () => {
      msgApi.success("已保存草稿");
      qc.invalidateQueries({ queryKey: ["evaluations", evaluation.guide_id] });
    },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const submitM = useMutation({
    mutationFn: () => submitEvaluation(evaluation.id),
    onSuccess: () => {
      msgApi.success("已提交,等级已判定并回写指南");
      qc.invalidateQueries({ queryKey: ["evaluations", evaluation.guide_id] });
      qc.invalidateQueries({ queryKey: [GUIDE_PICKER_KEY] });
      onClose();
    },
    onError: (e: Error) => msgApi.error(e.message),
  });

  const readOnly = evaluation.status !== "Waiting";

  const buildBody = (t: TemplateDetail) =>
    t.items.flatMap((it) =>
      it.entries.map((e) => ({
        item_id: it.id,
        entry_id: e.id,
        score: scores[`${it.id}-${e.id}`] ?? 0,
      })),
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={780}
      title={`${readOnly ? "查看" : "录入"}评审 #${evaluation.id}`}
      extra={
        !readOnly && (
          <Space>
            <Button
              loading={saveM.isPending}
              onClick={() => saveM.mutate(buildBody(tmplQ.data!))}
              disabled={!tmplQ.data}
            >保存草稿</Button>
            <Popconfirm
              title="确认提交?提交后不能再修改"
              onConfirm={async () => {
                // 先存草稿再提交
                if (!tmplQ.data) return;
                await saveM.mutateAsync(buildBody(tmplQ.data));
                submitM.mutate();
              }}
            >
              <Button type="primary" icon={<SendOutlined />} loading={submitM.isPending}>
                提交评审
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    >
      {msgCtx}
      {tmplQ.isLoading && <Spin />}
      {readOnly && (
        <Alert
          style={{ marginBottom: 12 }}
          type="success"
          message={`已提交:总分 ${evaluation.score}  等级 ${evaluation.level ?? "-"}`}
        />
      )}
      {tmplQ.data && (
        <>
          {tmplQ.data.items.map((it) => (
            <Card
              key={it.id}
              type="inner"
              size="small"
              title={`${it.name} (满分 ${it.score})`}
              style={{ marginBottom: 10 }}
            >
              {it.entries.map((e) => {
                const k = `${it.id}-${e.id}`;
                const cur = scores[k] ?? 0;
                return (
                  <Row key={e.id} gutter={8} style={{ padding: "6px 0" }} align="middle">
                    <Col flex={1}>
                      <Space direction="vertical" size={0}>
                        <span>{e.name}</span>
                        {e.description && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {e.description}
                          </Typography.Text>
                        )}
                      </Space>
                    </Col>
                    <Col>
                      <InputNumber
                        min={0}
                        max={Number(e.score) || 100}
                        value={cur}
                        disabled={readOnly}
                        onChange={(v) => setScores((p) => ({ ...p, [k]: Number(v ?? 0) }))}
                        addonAfter={`/${e.score}`}
                      />
                    </Col>
                  </Row>
                );
              })}
            </Card>
          ))}

          <Card type="inner" size="small" title="等级阈值参考">
            <Space>
              {tmplQ.data.levels.map((l) => (
                <Tag key={l.id} color="gold">
                  {l.name}: ≥ {l.score}
                </Tag>
              ))}
            </Space>
          </Card>
        </>
      )}
    </Drawer>
  );
}

// ===== P1.5 多专家评分对比(复刻旧站 inxGuideEvaluation/components/Entry)=====
// 并排展示同一 guide 各专家评审单按模板条目的打分;同一条目专家间分值不一致的行标红
// (旧站语义:与第一位专家不一致即标红,等价于「该行存在不一致分值」)。

interface CompareRow {
  key: string;
  label: string;
  scores: Record<string, string | null>;
  divergent: boolean;
}

function CompareDrawer({
  guide,
  evaluations,
  open,
  onClose,
}: {
  guide: GuideListItem;
  evaluations: EvaluationOut[];
  open: boolean;
  onClose: () => void;
}) {
  const first = evaluations[0];

  const tmplQ = useQuery({
    queryKey: ["template-detail", first?.template_id],
    queryFn: () => getTemplate(first!.template_id),
    enabled: first != null,
  });
  const expertsQ = useQuery({ queryKey: ["experts"], queryFn: listExperts });

  const expertName = (expertId: string) => expertDisplayName(expertId, expertsQ.data);

  const rows = useMemo<CompareRow[]>(() => {
    if (!tmplQ.data) return [];
    const out: CompareRow[] = [];
    for (const item of tmplQ.data.items) {
      for (const entry of item.entries) {
        const scores: Record<string, string | null> = {};
        for (const ev of evaluations) {
          scores[ev.id] = ev.entries.find((en) => en.entry_id === entry.id)?.score ?? null;
        }
        const filled = Object.values(scores).filter((s): s is string => s != null);
        out.push({
          key: entry.id,
          label: `${item.order_num}.${entry.order_num} ${entry.name}`,
          scores,
          divergent: new Set(filled).size > 1,
        });
      }
    }
    return out;
  }, [tmplQ.data, evaluations]);

  const diffCell = (row: CompareRow) => ({
    style: { background: row.divergent ? "rgba(255, 21, 21, 0.12)" : undefined },
  });

  const columns: TableProps<CompareRow>["columns"] = [
    { title: "评价项", dataIndex: "label", key: "label", onCell: diffCell },
    ...evaluations.map((ev) => ({
      title: (
        <Space direction="vertical" size={0}>
          <span>{expertName(ev.expert_id)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {ev.status === "Apply" ? `已提交 ${ev.score ?? "-"}分` : "待评"}
          </Typography.Text>
        </Space>
      ),
      key: String(ev.id),
      width: 110,
      align: "center" as const,
      onCell: diffCell,
      render: (_: unknown, row: CompareRow) => row.scores[ev.id] ?? "—",
    })),
  ];

  const mixedTemplates = new Set(evaluations.map((e) => e.template_id)).size > 1;
  const diffRows = rows.filter((r) => r.divergent).length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={900}
      title={
        <Space size={8} wrap>
          <span>多专家评分对比 · {guide.title}</span>
          {first?.is_divergent && (
            <Tag color="red">总分分歧 {first.diff_score} 分(≥3,建议组长复评)</Tag>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`同一评价项专家打分不一致的行已标红(共 ${diffRows} 行);总分差 ≥ 3 分判定分歧(对齐旧站组长复评阈值)。`}
      />
      {mixedTemplates && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="各评审单使用了不同模板,下表按第一份评审的模板条目对齐。"
        />
      )}
      <Table
        size="small"
        rowKey="key"
        loading={tmplQ.isLoading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ y: "65vh" }}
      />
    </Drawer>
  );
}
